import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { InputAdornment } from '@mui/material';

import { 
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Switch,
  Checkbox,
  Link,
  Container,
  FormHelperText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Stack
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { sendEmailCodePreRegister, checkInviteCode } from '../../api/modules/auth';
import { showMessage } from '../../utils';
const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#007AFF',
  padding: '12px',
  borderRadius: '24px',
  fontSize: '16px',
  fontWeight: 700,
  height: 48,
  boxShadow: '0 2px 8px rgba(0,51,161,0.08)',
  '&:hover': {
    backgroundColor: '#005BB5',
  },
}));

const SignUp = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  
  // 从URL获取inviteCode参数
  const searchParams = new URLSearchParams(window.location.search);
  const urlInviteCode = searchParams.get('inviteCode') || '';
  
  // 表单数据
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    newsletter: true,
    terms: true,
    inviteCode: urlInviteCode
  });

  // 错误信息
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms: '',
    inviteCode: ''
  });

  // 表单验证规则
  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    // 名字验证
    if (!formData.firstName.trim()) {
      tempErrors.firstName = 'First name is required';
      isValid = false;
    }

    if (!formData.lastName.trim()) {
      tempErrors.lastName = 'Last name is required';
      isValid = false;
    }

    // 手机号验证
    const phoneRegex = /^\d{11}$/;
    if (!formData.phone) {
      tempErrors.phone = 'Phone number is required';
      isValid = false;
    } else if (!phoneRegex.test(formData.phone.replace(/\D/g, ''))) {
      tempErrors.phone = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    // 邮箱验证
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!formData.email) {
      tempErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      tempErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // 密码验证
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formData.password) {
      tempErrors.password = 'Password is required';
      isValid = false;
    }
    // } else if (!passwordRegex.test(formData.password)) {
    //   tempErrors.password = 'Password must be at least 8 characters long and contain uppercase, lowercase, number and special character';
    //   isValid = false;
    // }

    // 确认密码验证
    if (!formData.confirmPassword) {
      tempErrors.confirmPassword = 'Please confirm your password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      tempErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // 服务条款验证
    if (!formData.terms) {
      tempErrors.terms = 'You must accept the Terms of Service and Privacy Policy';
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  // 处理输入变化
  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'newsletter' || name === 'terms' ? checked : value
    }));
    
    // 实时清除错误提示
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // 处理表单提交
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (validateForm()) {
      if (!formData.inviteCode.trim()) {
        // 如果没有填写邀请码，弹出确认对话框
        setOpenDialog(true);
      } else {
        checkInviteCode({ inviteCode: formData.inviteCode }, (rst) => {
          let { data } = rst;
          if (data) {
            handleEmailCodeSend();
          } else {
            setOpenDialog(true); // 弹出对话框
          }
        });
      }
    }
  };

    // 处理弹框关闭
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // 处理强制继续注册
  const handleForceContinue = async () => {
    setOpenDialog(false);
    await handleEmailCodeSend();
  };

  const handleEmailCodeSend = async () => {
      try {
        const registerData = {
          userType: '01',
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          subscribeFlag: formData.newsletter ? '1' : '0',
          email: formData.email,
          password: formData.password,
          repeatPassword: formData.confirmPassword,
          inviteType: 4,
          inviteCode: formData.inviteCode
        };
        await sendEmailCodePreRegister(registerData, (rst) => {
            let { code, data, msg } = rst;
            if (code == 200) {
              showMessage('Verification code sent successfully', 'success');
              setTimeout(() => {
                    // 发送成功，跳转到验证码页面，带上来源路由
                    navigate('/campaigns/invitations/verification', { 
                        state: { 
                        email: formData.email,
                        registerData,
                        from: location
                        }
                    });
              }, 1500);
            } else {
              showMessage(msg, 'error');
            }
          });
        
      } catch (error) {
        showMessage(error.message, 'error');
        setErrors(prev => ({
          ...prev,
          email: error.message
        }));
      }
  }

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#F7F9FC">
    {/* 左侧品牌区 */}
    <Box flex={1} display={{ xs: 'none', md: 'flex' }} flexDirection="column" alignItems="center" justifyContent="center">
      <Box component="img" src={`${process.env.PUBLIC_URL}/images/hello-door.png`} alt="Welcome" sx={{ width: 306, height: 241, mb: 4 }} />
      <Typography variant="h4" sx={{ color: '#031B4E', fontWeight: 700, fontSize: 36, mt: 2, textAlign: 'center' }}>
        Welcome Back!
      </Typography>
    </Box>

    {/* 右侧表单区 */}
    <Box flex={1} bgcolor="#fff" display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" px={3}>
      <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h4" sx={{ color: '#031B4E', fontWeight: 700, mb: 1, textAlign: 'center' }}>
          Sign Up
        </Typography>
        <Typography sx={{ color: '#031B4E', fontSize: 15, textAlign: 'center', mb: 3 }}>
          Already part of uTaxes? <Link onClick={() => navigate('/campaigns/invitations/login')} sx={{ color: '#007AFF', fontWeight: 600, cursor: 'pointer' }}>Log in</Link>
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              error={Boolean(errors.email)}
              helperText={errors.email}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              error={Boolean(errors.confirmPassword)}
              helperText={errors.confirmPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              error={Boolean(errors.phone)}
              helperText={errors.phone}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />
            <TextField
              fullWidth
              label="Invite Code"
              name="inviteCode"
              value={formData.inviteCode}
              onChange={handleChange}
              error={Boolean(errors.inviteCode)}
              helperText={errors.inviteCode}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#F8FAFC' } }}
            />

            <StyledButton
              fullWidth
              type="submit"
              variant="contained"
              size="large"
            >
              Sign Up
            </StyledButton>

            {/* Newsletter订阅 */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 1,
                gap: 1
              }}
            >
              <Switch 
                name="newsletter"
                checked={formData.newsletter}
                onChange={handleChange}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#007AFF'
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#007AFF'
                  }
                }}
              />
              <Typography 
                sx={{
                  fontSize: 15,
                  color: '#000'
                }}
              >
                Subscribe to our newsletters
              </Typography>
            </Box>

            {/* 服务条款 */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Checkbox 
                name="terms"
                checked={formData.terms}
                onChange={handleChange}
                sx={{
                  color: '#007AFF',
                  '&.Mui-checked': {
                    color: '#007AFF'
                  },
                  mt: '-2px'
                }}
              />
              <Box>
                <Typography 
                  sx={{
                    fontSize: 13,
                    color: '#666',
                    lineHeight: 1.4
                  }}
                >
                  I have read and agree: 
                  <Link 
                    href="https://utaxes.com/help/policies/terms-of-service" 
                    sx={{ 
                      color: '#007AFF',
                      textDecoration: 'none',
                      mx: '2px'
                    }}
                  >
                    Terms of Service
                  </Link>
                  and
                  <Link 
                    href="https://utaxes.com/help/policies/privacy-policy" 
                    sx={{ 
                      color: '#007AFF',
                      textDecoration: 'none',
                      ml: '2px'
                    }}
                  >
                    Privacy Policy
                  </Link>
                </Typography>
                {errors.terms && (
                  <FormHelperText error>
                    {errors.terms}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
         {/* 添加确认弹框 */}
         <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            padding: '16px'
          }
        }}
      >
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: '#333',
              fontSize: '16px',
              textAlign: 'center',
              mb: 2
            }}
          >
            The invite code is not provided. Do you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              width: '120px',
              color: '#666',
              borderColor: '#666',
              '&:hover': {
                borderColor: '#333',
                color: '#333'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForceContinue}
            variant="contained"
            sx={{
              borderRadius: '8px',
              width: '120px',
              bgcolor: '#1976d2',
              '&:hover': {
                bgcolor: '#1565c0'
              }
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
  </Box>

  );
};

export default SignUp;